import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HandHeart, Sparkles, CheckCircle2, Send, StopCircle } from "lucide-react";

const ST_LABEL = { active: "Aktywna", completed: "Zakończona", stopped: "Zatrzymana" };
const fmt = (d) => (d ? new Date(d).toLocaleString("pl-PL") : "—");
const reason = (r) => {
  if (!r) return "";
  if (r.startsWith("manual:")) return `zatrzymana ręcznie przez ${r.slice(7)}`;
  return { unsubscribed: "klient wypisał się", client_removed: "karta klienta usunięta", expired: "wygasła przed zatwierdzeniem serii" }[r] || r;
};

function StepEditor({ i, day, step, onChange, onTest }) {
  return (
    <div className="border border-border rounded-md p-3 space-y-2 min-w-0" data-testid={`welcome-step-${i}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-[#193962] bg-[#193962]/10 rounded-full px-2 py-0.5">E-mail {i + 1} · dzień {day}</span>
        <Button size="sm" variant="ghost" className="ml-auto h-7" onClick={onTest} data-testid={`welcome-test-${i}`}><Send className="h-3.5 w-3.5 mr-1" />Test do mnie</Button>
      </div>
      <Input data-testid={`welcome-subject-${i}`} placeholder="Temat" value={step.subject} onChange={(e) => onChange({ ...step, subject: e.target.value })} />
      <Textarea data-testid={`welcome-body-${i}`} rows={5} placeholder="Treść HTML — {{firma}} {{osoba}} {{kraj}}" value={step.body_html} onChange={(e) => onChange({ ...step, body_html: e.target.value })} className="font-mono text-xs" />
    </div>
  );
}

function Enrollments({ list, onStop }) {
  if (!list.length) return <p className="text-sm text-muted-foreground font-[Manrope]" data-testid="welcome-enroll-empty">Nikt nie jest jeszcze zapisany. Konwersja leada na klienta zapisuje nową kartę automatycznie.</p>;
  return (
    <div className="divide-y divide-border max-h-72 overflow-auto">
      {list.map((e) => (
        <div key={e.enrollment_id} className="flex items-center gap-3 py-2 text-sm" data-testid={`welcome-enroll-${e.enrollment_id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate"><Link to={`/clients/${e.client_id}`} className="hover:underline">{e.company_name || e.email}</Link></p>
            <p className="text-[11px] text-zinc-500 truncate">{e.email} · wysłano {e.sent?.length || 0}/3 · {e.status === "active" ? `następny ${fmt(e.next_due_at)}` : reason(e.stop_reason)}</p>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600" data-testid={`welcome-enroll-status-${e.enrollment_id}`}>{ST_LABEL[e.status]}</span>
          {e.status === "active" && <Button size="sm" variant="ghost" onClick={() => onStop(e.enrollment_id)} data-testid={`welcome-stop-${e.enrollment_id}`} className="text-red-600"><StopCircle className="h-3.5 w-3.5 mr-1" />Zatrzymaj</Button>}
        </div>
      ))}
    </div>
  );
}

export const WelcomeSeriesPanel = () => {
  const [s, setS] = useState(null);
  const [enr, setEnr] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/welcome-series").then((r) => setS(r.data)).catch(() => {});
    api.get("/welcome-series/enrollments").then((r) => setEnr(r.data.enrollments)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!s) return null;

  const complete = s.steps.every((st) => st.subject?.trim() && st.body_html?.trim());
  const setStep = (i, v) => setS((x) => ({ ...x, approved: false, dirty: true, steps: x.steps.map((st, j) => (j === i ? v : st)) }));
  const err = (e) => toast.error(e?.response?.data?.detail || "Błąd");
  const save = async () => { try { await api.put("/welcome-series", { steps: s.steps }); toast.success("Zapisano — zatwierdź, aby rozpocząć wysyłkę"); load(); } catch (e) { err(e); } };
  const generate = async () => {
    setBusy(true);
    try { const r = await api.post("/welcome-series/generate"); setS((x) => ({ ...x, steps: r.data.steps, approved: false, dirty: true })); toast.success("Szkic AI gotowy — sprawdź i zapisz"); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  const approve = async () => { try { await api.post("/welcome-series/approve"); toast.success("Seria zatwierdzona — wysyłka włączona"); load(); } catch (e) { err(e); } };
  const test = async (i) => { try { const r = await api.post(`/welcome-series/test-send/${i}`); toast.success(`Wysłano test do ${r.data.sent_to}`); } catch (e) { err(e); } };
  const stop = async (id) => { try { await api.post(`/welcome-series/enrollments/${id}/stop`); toast.success("Seria zatrzymana dla tego klienta"); load(); } catch (e) { err(e); } };

  return (
    <section className="border border-border bg-white rounded-md p-5 mt-6 space-y-4" data-testid="welcome-series-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <HandHeart className="h-4 w-4 text-[#193962]" />
        <h3 className="font-[Outfit] font-semibold">Seria powitalna</h3>
        <span data-testid="welcome-status" className={`text-[10px] px-2 py-0.5 rounded-full ${s.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{s.approved ? "Zatwierdzona · wysyłka" : s.dirty ? "Niezapisane zmiany" : "Niezatwierdzona · wstrzymana"}</span>
        <div className="flex gap-1.5 ml-auto flex-wrap">
          <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="welcome-generate-btn"><Sparkles className="h-3.5 w-3.5 mr-1" />{busy ? "Piszę…" : "Szkic AI"}</Button>
          <Button size="sm" onClick={save} data-testid="welcome-save-btn" className="bg-[#193962] text-white">Zapisz</Button>
          {!s.approved && !s.dirty && complete && <Button size="sm" onClick={approve} data-testid="welcome-approve-btn" className="bg-emerald-600 text-white"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Zatwierdź</Button>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground font-[Manrope]">Wysyłana od Bena do każdej nowej karty klienta utworzonej z leada. Zatrzymuje się, gdy klient się wypisze. Edycja wstrzymuje wysyłkę do ponownego zatwierdzenia.</p>
      <div className="grid lg:grid-cols-3 gap-3">
        {s.steps.map((st, i) => <StepEditor key={i} i={i} day={s.days[i]} step={st} onChange={(v) => setStep(i, v)} onTest={() => test(i)} />)}
      </div>
      <div>
        <h4 className="font-[Outfit] text-sm font-semibold mb-2">Zapisani klienci <span className="text-xs text-muted-foreground" data-testid="welcome-enroll-count">({enr.length})</span></h4>
        <Enrollments list={enr} onStop={stop} />
      </div>
    </section>
  );
};
